import lodash from 'lodash';
import type { QueryObject } from 'interface/queryParam';
import config from '../config';
import pinterpolate from 'pinterpolate';

/**
 * Build supplied string by interpolating properties after delimiter(':') with the given parameters.
 *
 * @example
 * interpolate('/posts/:id', {id: 1})
 * => '/posts/1'
 *
 * @param str
 * @param params
 * @returns */
export const interpolate = (str: string, params: Record<string, string | number>): string => pinterpolate(str, params);

/**
 * Extracts fullname from user object.
 *
 * @param Object
 * @returns */
export const extractFullName = (
  { firstName, middleName, lastName }: { firstName: string; middleName?: string | null; lastName: string },
  includeMiddleName = false
): string => `${firstName} ${includeMiddleName && middleName ? middleName + ' ' : ''}${lastName}`;

/**
 * Extracts month and day from date
 *
 * @param date
 */
export function stripYear(date: Date): string {
  return date.toDateString().slice(4, 10);
}

/**
 *
 * Parses query to usable object
 *
 * @param query
 * @param hasArray
 */
export const parseQuery = (query: string, hasArray = true): Record<string, string | string[]> => {
  return query
    .substring(1, query.length)
    .split('&')
    .reduce((acc, curr) => {
      const items = curr.split('=');
      items[1] = items[1] ? items[1].replace(/%20/g, ' ') : items[1];

      if (hasArray) {
        const keySize = items[0].length;

        if (keySize > 2 && items[0].substring(keySize - 2, keySize) === '[]') {
          const newKey = items[0].substring(0, keySize - 2);
          return {
            ...acc,
            [newKey]: acc[newKey] ? [...acc[newKey], items[1]] : [items[1]],
          };
        }
      }

      return items[0] && items[1]
        ? {
            ...acc,
            [items[0]]: decodeURIComponent(items[1]),
          }
        : acc;
    }, {});
};

/**
 *
 * Unparses object to query
 *
 * @param query
 */
export const unParseQuery = (object: QueryObject): string => {
  return Object.keys(object)
    .reduce((acc, curr) => {
      const raw = object[curr];

      if (Array.isArray(raw)) {
        const joinedValues = raw.map(String).join(`&${curr}[]=`);
        return `${acc}${curr}[]=${joinedValues}&`;
      }

      return curr && raw ? `${acc}${curr}=${encodeURIComponent(String(raw))}&` : acc;
    }, '?')
    .slice(0, -1);
};

/**
 * Formats URL to be used in the browser.
 *
 * @param url
 * @returns */
export function formatURL(string: string): string {
  return string.includes('://') ? string : '//' + string;
}

/**
 * Formats text to starting letter uppercase.
 *
 * @param text
 * @returns */
export function startCase(text: string): string {
  return lodash.startCase(text.toLowerCase());
}

/**
 * Function to pluralize the given string with given suffix.
 * for eg: result ==> results (by default).
 *
 * @param string
 * @param count
 * @param suffix
 * @returns
 */
export const pluralize = (string: string, count = 2, suffix = 's'): string =>
  count >= 2 || count === 0 ? string + suffix : string;

/**
 * Returns random color
 *
 * @returns */
export function getRandomColor(): string {
  return '#' + Math.floor(Math.random() * 16777215).toString(16);
}

/* Truncate a string to num characters.
 *
 * @param str
 * @param num
 */
export const truncate = (str: string, num: number): string => (str.length > num ? str.slice(0, num) + ' ...' : str);

/**
 * @param label
 * @returns
 */
export const getRequiredErrorLabel = (label: string): string => `Please provide ${label}`;

/**
 *
 * @param label
 * @returns */
export const getDuplicateErrorLabel = (label: string): string => `${label} already exists`;

/**
 *
 * @returns Unique value on each call
 */
export const getUniqueKey = () => Math.floor(Math.random() * Date.now());

/**
 * Add zero to the left of the number if it is less than given place value for better formatting.
 * for eg: 12 ==> '12'  1 ==> '01'
 *
 * @param number
 * @param digitCount
 * @returns */
export const padZero = (number = 0, requiredLength = 2): string | number => {
  const currentLength = number.toString().length;

  const neededToPadLength = requiredLength - currentLength;

  return neededToPadLength > 0 ? `${'0'.repeat(neededToPadLength)}${number}` : number;
};

/**
 * Formats given time to 00 : 00 AM format.
 * for eg : (1,AM) is converted 01:00 AM
 *
 * @param time
 * @param amOrPM
 * @returns */
export const formatTime = (time = '', amOrPM?: string): string => {
  const hours = +String(time).slice(0, 2) || 0;
  const mins = +String(time).slice(3, 5) || 0;

  const base = `${padZero(hours)}:${padZero(mins)}`;
  return amOrPM ? `${base} ${amOrPM}` : base;
};

/**
 * Employee Profile image url
 *
 * @param empId
 * @returns */

export const getEmpImageUrl = (empId: string | number | null | undefined, resolution = ''): string => {
  const empImageURI = config.empImageURI || '';
  const resolvedEmpId = String(empId ?? '');

  if (resolution) {
    return interpolate(empImageURI.replace('48', resolution), { empId: resolvedEmpId });
  }

  return interpolate(empImageURI, { empId: resolvedEmpId });
};

/**
 *
 * @param formattedTime
 * @returns */
export const getTime = (formattedTime: string): { type: string; value: string } => {
  const type = formattedTime.slice(-2);
  const value = formattedTime.slice(0, 5);

  return { type, value };
};

export const underscoreToSpace = (text: string): string => text && text.split('_').join(' ').toLowerCase();

/**
 *
 * @param str
 * @returns
 */
export const isStringEmpty = (str: string): boolean => {
  return !str || str.trimStart().length === 0;
};

/**
 *
 * @param str
 * @param minLength
 * @returns */
export const checkStringLength = (str: string, minLength: number): boolean => {
  return str.trimStart().length < minLength;
};

/**
 * Capitalize a string.
 *
 * @param str
 * @returns */
export const capitalize = (str: string): string => str.charAt(0).toUpperCase() + str.slice(1);

/**
 * Build a Url removing multiple slashes and trailing slash.
 *
 * @param routes
 * @returns *
 * @example
 * buildUrl('http://localhost:3000', 'api', 'v1', 'users', '1') // http://localhost:3000/api/v1/users/1
 * buildUrl('http://localhost:3000/', '//api/', '/v1/', '///users///', '//1////')
 * // http://localhost:3000/api/v1/users/1
 *
 * buildUrl('google.com', 'searchText') // google.com/searchText
 * buildUrl('google.com', '/searchText') // google.com/searchText
 * buildUrl('google.com/', '/searchText') // google.com/searchText
 * buildUrl('//google.com/', '/searchText') // //google.com/searchText
 * buildUrl('https://google.com/', '/searchText') //  https://google.com/searchText
 * buildUrl('http://google.com/', '/searchText') // http://google.com/searchText
 * buildUrl("http://google.com/", "/searchText?redirectUrl=https://google.com");
 * // http://google.com/searchText?redirectUrl=https://google.com
 */
export const buildUrl = (...routes: string[]): string => {
  return routes
    .join('/')
    .replace(/(https?:\/\/|^\/\/)|(\/)+/g, '$1$2') // remove double slashes
    .replace(/\/+$/, ''); // remove trailing slash
};

/**
 * Remove https://
 * @param url
 * @returns * @example withoutProtocolURL('https://github.com/leapfrogtechnology/vyaguta-okr/pull/96')
 * @return 'github.com/leapfrogtechnology/vyaguta-okr/pull/96'
 */
export const withoutProtocolURL = (url: string): string => {
  if (!url) {
    return url;
  }

  const arr = url.split('//');

  if (arr.length === 1) {
    return url;
  }

  return url.split('//')[1];
};

/**
 * Remove https:// and www.
 * @param url
 * @returns * @example withoutProtocolAndSubDomainURL('https://www.google.com')
 * @return 'google.com'
 */
export const withoutProtocolAndSubDomainURL = (url: string): string => {
  if (!url) return url;

  let withoutProtocol = url;

  if (url.startsWith('http://') || url.startsWith('https://')) {
    withoutProtocol = url.split('//')[1];
  }

  if (withoutProtocol.startsWith('www.')) {
    return withoutProtocol.slice(4);
  }

  return withoutProtocol;
};

/**
 * Converts an strings to a comma-separated array of string.
 * @param value
 * @returns splitString
 * @example stringToArray('1,2,3,4,5',',')
 * @return ['1','2','3','4','5']
 */
export const stringToArray = (value: string, splitString = ',', toNumber = false): string[] | number[] => {
  if (toNumber) {
    return value?.split(splitString).map(Number);
  }

  return value?.split(splitString);
};

/**
 * Converts an array of strings to a comma-separated string.
 * @param value
 * @returns splitString
 * @example arrayToString(['1','2','3','4','5'])
 * @return '1,2,3,4,5'
 */
export const arrayToString = (value: Array<string | number>, joinString = ','): string => {
  return value?.join(joinString);
};

/**
 * Returns the first and last name from a full name with middle name(s).
 * @param fullName - The full name with middle name(s).
 * @returns The first and last name.
 * @example
 * getFirstAndLastName("John Smith"); // returns "John Smith"
 * getFirstAndLastName("John A. Smith"); // returns "John Smith"
 * getFirstAndLastName("John Adam Smith"); // returns "John Smith"
 * getFirstAndLastName(""); // returns ""
 */
export const getFirstAndLastName = (fullName: string): string => {
  if (!fullName) {
    return '';
  }

  const names = fullName.split(' ');

  if (names.length <= 2) {
    return fullName;
  }

  const firstName = names[0];
  const lastName = names[names.length - 1] || '';

  return `${firstName} ${lastName}`;
};

/**
 * Returns the name with a possession suffix.
 *
 * @param name
 * @returns *
 * @example
 * getWithPossessionSuffix('John'); // returns "John's"
 * getWithPossessionSuffix('Johns'); // returns "Johns'"
 * getWithPossessionSuffix('John Smith'); // returns "John Smith's"
 * getWithPossessionSuffix('John Smiths'); // returns "John Smiths'"
 */
export const getWithPossessionSuffix = (name: string): string => {
  if (name.endsWith('s')) {
    return `${name}'`;
  }

  return `${name}'s`;
};

/**
 * Returns the first from a full name
 * @param fullName - The full name with middle name(s).
 * @returns The first
 * @example
 * getFirstAndLastName("John Smith"); // returns "John"
 * getFirstAndLastName("John A. Smith"); // returns "John"
 * getFirstAndLastName("John Adam Smith"); // returns "John"
 * getFirstAndLastName(""); // returns ""
 */
export const getFirstName = (fullName: string): string => {
  if (!fullName) {
    return '';
  }

  const names = fullName.split(' ');

  const firstName = names[0];

  return firstName;
};

/**
 * Project logo url
 *
 * @param projectId
 * @returns */
export const getProjectLogoUrl = (projectId: string | number): string =>
  interpolate(config.projectLogoURI, { projectId });

/**
 * Get the initials of a given name.
 *
 * @param name - The full name from which to derive initials.
 * @returns The initials of the given name, in uppercase.
 *
 * @example
 * // Returns 'JD'
 * getInitials('John Doe');
 *
 * @example
 * // Returns 'JSD'
 * getInitials('John Smith Doe');
 */
export const getInitials = (name: string): string => {
  const words = name.trim().split(' ');

  return words.map((word) => word.charAt(0).toUpperCase()).join('');
};
